// src/components/Settings/Layout/ImageSettingsPopup.js

import React, { useState, useEffect, useRef } from "react";
import "../Styles/ImageSettingsPopup.css";
import kleidoLogo from "../../../../../assets/images/Kleido.png"

// ── Virtual avatar options ────────────────────────────────────────────────
const VIRTUAL_AVATARS = [
  { id: "av1",  emoji: "🦊", label: "Fox"        },
  { id: "av2",  emoji: "🐺", label: "Wolf"       },
  { id: "av3",  emoji: "🦋", label: "Butterfly"  },
  { id: "av4",  emoji: "🐉", label: "Dragon"     },
  { id: "av5",  emoji: "🦁", label: "Lion"       },
  { id: "av6",  emoji: "🐬", label: "Dolphin"    },
  { id: "av7",  emoji: "🦅", label: "Eagle"      },
  { id: "av8",  emoji: "🐼", label: "Panda"      },
  { id: "av9",  emoji: "🦄", label: "Unicorn"    },
  { id: "av10", emoji: "🐙", label: "Octopus"    },
  { id: "av11", emoji: "🦋", label: "Phoenix"    },
  { id: "av12", emoji: "🤖", label: "Robot"      },
];

// ── Visibility options ────────────────────────────────────────────────────
const VISIBILITY_OPTIONS = [
  {
    key:      "public",
    icon:     "🌐",
    label:    "Public",
    desc:     "Everyone can see your profile photo, including people not on your friends list.",
    color:    "white",
  },
  {
    key:      "friends",
    icon:     "👨‍👩‍👧‍👦",
    label:    "Friends & Family",
    desc:     "Only people you are connected with can see your photo.",
    color:    "white",
  },
  {
    key:      "none",
    icon:     "🚫",
    label:    "No One (Hidden)",
    desc:     "Your profile photo is completely hidden from all users. A placeholder is shown.",
    color:    "white",
  },
];

// ==================== POPUP COMPONENT ====================

const ImageSettingsPopup = ({
  initialVisibility    = "public",
  initialVirtualImage  = null,
  initialAllowMaximize = true,
  onSave,
  onClose,
}) => {
  const [visibility,    setVisibility]    = useState(initialVisibility);
  const [virtualImage,  setVirtualImage]  = useState(initialVirtualImage);
  const [allowMaximize, setAllowMaximize] = useState(initialAllowMaximize);
  const [activeTab,     setActiveTab]     = useState("visibility"); // 'visibility' | 'virtual' | 'behaviour'
  const [closing,       setClosing]       = useState(false);

  const backdropRef = useRef(null);

  // ── ESC key to close ──────────────────────────────────────────────────
  useEffect(() => {
    const handler = (e) => { if (e.key === "Escape") handleClose(); };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, []);

  // ── Animate out before unmounting ─────────────────────────────────────
  const handleClose = () => {
    setClosing(true);
    setTimeout(onClose, 220);
  };

  const handleBackdropClick = (e) => {
    if (e.target === backdropRef.current) handleClose();
  };

  const handleSave = () => {
    onSave({ imageVisibility: visibility, virtualImage, allowMaximize });
  };

  // ── Derived: preview display ──────────────────────────────────────────
  const currentVirtual  = VIRTUAL_AVATARS.find(a => a.id === virtualImage);
  const visOption       = VISIBILITY_OPTIONS.find(v => v.key === visibility);

  return (
    <div
      ref={backdropRef}
      className={`isp-backdrop ${closing ? "isp-backdrop--out" : ""}`}
      onClick={handleBackdropClick}
    >
      <div className={`isp-modal ${closing ? "isp-modal--out" : ""}`}>

        {/* ── Modal header ──────────────────────────────────────────── */}
        <div className="isp-header">
          <div className="isp-header-left">
            <img src={kleidoLogo} alt="Kleido" className="h-12 w-12 rounded-lg" />
            <div>
              <h2 className="isp-title">Profile Image Settings</h2>
              <p  className="isp-subtitle">Control visibility, avatar & interaction behaviour</p>
            </div>
          </div>
          <button className="isp-close-btn" onClick={handleClose} title="Close">×</button>
        </div>

        {/* ── Live preview strip ────────────────────────────────────── */}
        <div className="isp-preview-strip">
          <div className="isp-preview-avatar">
            {currentVirtual
              ? <span className="isp-preview-emoji">{currentVirtual.emoji}</span>
              : <span className="isp-preview-emoji">👤</span>
            }
            {visibility === "none" && <div className="isp-preview-overlay">🚫</div>}
          </div>
          <div className="isp-preview-details">
            <div className="isp-preview-row">
              <span className="isp-preview-chip"
                style={{ background: visOption?.selected, borderColor: visOption?.border, color: visOption?.color }}
              >
                {visOption?.icon} {visOption?.label}
              </span>
              <span className="isp-preview-chip isp-preview-chip--neutral">
                {allowMaximize ? "🔍 Zoom enabled" : "🔒 Zoom disabled"}
              </span>
              {currentVirtual && (
                <span className="isp-preview-chip isp-preview-chip--virtual">
                  🎭 Virtual: {currentVirtual.label}
                </span>
              )}
            </div>
            <p className="isp-preview-hint">Live preview of your current selections</p>
          </div>
        </div>

        {/* ── Tab bar ───────────────────────────────────────────────── */}
        <div className="isp-tabs">
          {[
            { key: "visibility", label: "👁 Visibility"     },
            { key: "virtual",    label: "🎭 Virtual Avatar" },
            { key: "behaviour",  label: "🖱 Behaviour"      },
          ].map(t => (
            <button
              key={t.key}
              className={`isp-tab ${activeTab === t.key ? "isp-tab--active" : ""}`}
              onClick={() => setActiveTab(t.key)}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* ── Tab content ───────────────────────────────────────────── */}
        <div className="isp-body">

          {/* ┌─ TAB 1: Visibility ──────────────────────────────────── */}
          {activeTab === "visibility" && (
            <div className="isp-tab-content">
              <p className="isp-section-desc">
                Choose who can view your profile photo across the platform.
              </p>
              <div className="isp-vis-options">
                {VISIBILITY_OPTIONS.map(opt => (
                  <button
                    key={opt.key}
                    type="button"
                    className={`isp-vis-card ${visibility === opt.key ? "isp-vis-card--selected" : ""}`}
                    style={{
                      borderColor: visibility === opt.key ? opt.border : "rgba(255,255,255,0.08)",
                      background:  visibility === opt.key ? opt.selected : "rgba(255,255,255,0.02)",
                    }}
                    onClick={() => setVisibility(opt.key)}
                  >
                    <div className="isp-vis-card-top">
                      <span className="isp-vis-icon">{opt.icon}</span>
                      <div className="isp-vis-info">
                        <span
                          className="isp-vis-label"
                          style={{ color: visibility === opt.key ? opt.color : "rgba(255,255,255,0.80)" }}
                        >
                          {opt.label}
                        </span>
                        <span className="isp-vis-desc">{opt.desc}</span>
                      </div>
                      <span
                        className="isp-vis-radio"
                        style={{
                          borderColor: visibility === opt.key ? opt.color : "rgba(255,255,255,0.22)",
                          background:  visibility === opt.key ? opt.color : "transparent",
                        }}
                      />
                    </div>
                  </button>
                ))}
              </div>

              {visibility === "none" && (
                <div className="isp-warning-note">
                  <span>ℹ️</span>
                  <p>
                    When hidden, a generic placeholder will display on your profile.
                    People will not be able to see or download your actual photo.
                  </p>
                </div>
              )}
            </div>
          )}

          {/* ┌─ TAB 2: Virtual Avatar ──────────────────────────────── */}
          {activeTab === "virtual" && (
            <div className="isp-tab-content">
              <p className="isp-section-desc">
                Choose a virtual avatar to display <em>instead of</em> your real photo.
                Your actual image is kept private when a virtual avatar is active.
              </p>

              {/* Clear selection */}
              {virtualImage && (
                <button
                  type="button"
                  className="isp-clear-avatar-btn"
                  onClick={() => setVirtualImage(null)}
                >
                  ✕ Clear virtual avatar — use real photo
                </button>
              )}

              <div className="isp-avatar-grid">
                {VIRTUAL_AVATARS.map(av => (
                  <button
                    key={av.id}
                    type="button"
                    className={`isp-avatar-card ${virtualImage === av.id ? "isp-avatar-card--selected" : ""}`}
                    onClick={() => setVirtualImage(virtualImage === av.id ? null : av.id)}
                    title={av.label}
                  >
                    <span className="isp-avatar-emoji">{av.emoji}</span>
                    <span className="isp-avatar-label">{av.label}</span>
                    {virtualImage === av.id && (
                      <span className="isp-avatar-check">✓</span>
                    )}
                  </button>
                ))}
              </div>

              <div className="isp-warning-note" style={{ marginTop: 20, background: "rgba(36,36,45,0.2)", border: "1px solid white" }}>
                <span>🎭</span>
                <p>
                  A virtual avatar replaces your profile photo everywhere on the platform.
                  Your real photo is never shown when an avatar is active.
                </p>
              </div>
            </div>
          )}

          {/* ┌─ TAB 3: Behaviour ───────────────────────────────────── */}
          {activeTab === "behaviour" && (
            <div className="isp-tab-content">
              <p className="isp-section-desc">
                Control what happens when someone interacts with your profile image.
              </p>

              <div className="isp-behaviour-card">
                <div className="isp-behaviour-left">
                  <span className="isp-behaviour-icon">{allowMaximize ? "🔍" : "🔒"}</span>
                  <div className="isp-behaviour-info">
                    <span className="isp-behaviour-title">
                      Click to Maximise
                    </span>
                    <span className="isp-behaviour-desc">
                      {allowMaximize
                        ? "Clicking your profile photo opens a fullscreen lightbox view. Viewers can zoom in."
                        : "Clicking your profile photo does nothing. No enlarged view is shown to anyone."
                      }
                    </span>
                  </div>
                </div>
                <label className="st-switch" style={{ flexShrink: 0 }}>
                  <input
                    type="checkbox"
                    checked={allowMaximize}
                    onChange={() => setAllowMaximize(p => !p)}
                  />
                  <span className="st-slider" />
                </label>
              </div>

              {/* Visual demo */}
              <div className="isp-behaviour-demo">
                <div className="isp-behaviour-demo-label">Preview</div>
                <div
                  className={`isp-behaviour-demo-avatar ${allowMaximize ? "isp-behaviour-demo-avatar--clickable" : ""}`}
                  title={allowMaximize ? "Click to maximise (enabled)" : "Click to maximise (disabled)"}
                  style={{border: "white"}}
                >
                  <span style={{ fontSize: "2.2rem" }}>
                    {currentVirtual ? currentVirtual.emoji : "👤"}
                  </span>
                  {allowMaximize && (
                    <div className="isp-behaviour-demo-zoom">🔍</div>
                  )}
                </div>
                <p className="isp-behaviour-demo-hint">
                  {allowMaximize
                    ? "Hover shows zoom hint · Click opens full view"
                    : "No interaction on click"
                  }
                </p>
              </div>

              {!allowMaximize && (
                <div className="isp-warning-note">
                  <span>ℹ️</span>
                  <p>
                    When maximise is disabled, your photo cannot be opened in full view by anyone,
                    which may help prevent downloading of your photo.
                  </p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* ── Footer actions ────────────────────────────────────────── */}
        <div className="isp-footer">
          <button type="button" className="isp-cancel-btn" onClick={handleClose}>
            Cancel
          </button>
          <button type="button" className="isp-save-btn" onClick={handleSave}>
            💾 Save Image Settings
          </button>
        </div>

      </div>
    </div>
  );
};

export default ImageSettingsPopup;
